import { Component, Input, OnInit, ViewChild } from '@angular/core';
import moment from 'moment-timezone';
import { Observable, Subscription, switchMap, tap, map, filter, take, iif, of } from 'rxjs';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { OptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { CoverageAnalysisService, InsightContext } from '../../../../Monitor/Shared/services/coverage-analysis.service';
import { CustomInsightType } from '../../../../Monitor/Shared/models/custom-insights-type.model';
import { CustomInsights } from '../../../../Monitor/Shared/models/custom-insights.model';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';

enum DropDownOptionGroupKey {
    BREFINGS = 'BREFINGS',
    MONITORING = 'MONITORING',
}

enum DropdownGroupOptionType {
    GROUP = 'GROUP'
}

class GroupOptionModel<T = any, U = any> extends OptionModel {
    private readonly groupItemOptions: OptionModel<U>[];
    optionType = DropdownGroupOptionType.GROUP;

    constructor(label: string, value: T, options: OptionModel<U>[] = []) {
        super(label, value);
        this.groupItemOptions = options;
    }
    *[Symbol.iterator](): IterableIterator<OptionModel<T> | OptionModel<U>> {
        yield this;
        yield* this.groupItemOptions;
    }
}

interface DialogCustomInsights extends CustomInsights {
    tooltip?: string;
}

enum ContentMode {
    UPDATE = 'UPDATE',
    CREATE = 'CREATE',
    DELETE = 'DELETE'
}



@Component({
    selector: 'agpr-custom-prompts-management-dialog',
    templateUrl: './custom-prompts-management-dialog.component.html',
    styleUrls: ['./custom-prompts-management-dialog.component.scss'],
    standalone: false
})
export class CustomPromptsManagementDialogComponent extends AbstractDialogComponent implements OnInit {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly promptType = CustomInsightType;
    readonly dropdownGroupOptionType = DropdownGroupOptionType;
    readonly contentMode = ContentMode;

    @ViewChild('listScrollPanel') listScrollPanelElement!: ScrollPanelComponent;

    @Input() id: string = 'customPromptsManagementDialog';
    @Input() animationTrigger: Observable<FullProfileAnimationModel> | null = null;
    @Input() overlayContainer: Element | null = null;
    @Input() selectedPromptType: CustomInsightType = CustomInsightType.BRIEFING_ANALYSIS_BLOCK;

    hideSlideOverlay: boolean = false;
    isLoading: boolean = false;
    subscriptions: Subscription[] = [];

    dropdownPromptTypes: OptionModel<string>[] = [
        ...new GroupOptionModel('CustomPrompt.prompt.dropdown.briefings.header', DropDownOptionGroupKey.BREFINGS,
            [new OptionModel('CustomPrompt.prompt.dropdown.briefings.list.ai', CustomInsightType.BRIEFING_ANALYSIS_BLOCK)]
        ),
        ...new GroupOptionModel('CustomPrompt.prompt.dropdown.monitoring.header', DropDownOptionGroupKey.MONITORING,
            [
                new OptionModel('CustomPrompt.prompt.dropdown.monitoring.list.insight', CustomInsightType.MENTION_INSIGHTS),
                new OptionModel('CustomPrompt.prompt.dropdown.monitoring.list.coverageCuration', CustomInsightType.COVERAGE_CURATION)
            ]
        )
    ];

    promptsList: Record<CustomInsightType, Array<DialogCustomInsights>> = {
        [CustomInsightType.MENTION_INSIGHTS]: [],
        [CustomInsightType.BRIEFING_ANALYSIS_BLOCK]: [],
        [CustomInsightType.BRIEFING_CURATOR]: [],
        [CustomInsightType.COVERAGE_CURATION]: [],
    };

    editingContent: Pick<CustomInsights, 'name' | 'prompt'> = {
        name: '',
        prompt: ''
    };

    selectedContent: Pick<CustomInsights, 'name' | 'prompt'> = {
        name: '',
        prompt: ''
    };

    userIdToNameMap: Record<number, string> = {};
    editingContentUpdated = false;
    editingMode: ContentMode = ContentMode.UPDATE;
    /** Prompt to open on. Null opens on the first of the selected type, as it always has */
    @Input() selectedInsightId: number | null = null;

    private dateFormat: string = '';

    constructor(coreDialogService: CoreDialogService,
        private coverageAnalysisService: CoverageAnalysisService,
        private userService: UsersService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'CustomPromptsManagementDialogComponent');
    }

    // Loads in ngOnInit, not the constructor - the dialog service sets inputs such as
    // selectedInsightId after construction, and the cached prompts can emit synchronously
    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x))
                .pipe(take(1)).subscribe(prefs => {
                    this.dateFormat = prefs.getDateFormatString(true);
                    this.initCustomInsights();
                }),
        );
    }

    private initCustomInsights() {
        this.isLoading = true;
        const requestCustomInsights$ = this.coverageAnalysisService.customInsights$.pipe(
            tap((items: CustomInsights[]) => {
                this.promptsList[CustomInsightType.MENTION_INSIGHTS] = [];
                this.promptsList[CustomInsightType.BRIEFING_ANALYSIS_BLOCK] = [];
                this.promptsList[CustomInsightType.BRIEFING_CURATOR] = [];
                this.promptsList[CustomInsightType.COVERAGE_CURATION] = [];

                items.forEach(item => {
                    this.promptsList[item.promptType]?.push(item);
                });
            }),
            map((items: CustomInsights[]) => {
                return [...new Set(
                    items
                        .flatMap(item => [item?.createdByUserId, item?.lastModifiedByUserId])
                        .filter((id): id is number => id != null)
                        .filter(id => !this.userIdToNameMap[id])  // Exclude user IDs whose names have already been loaded
                )];
            }),
            switchMap((userIds: number[]) => {
                return userIds.length
                    ? this.userService.getUsers(userIds)
                    : of([]);
            })
        ).subscribe({
            next: (users: UserModel[]) => {
                users.forEach(user => {
                    const userId = user.id as number;
                    this.userIdToNameMap[userId] =
                        `${user.firstName} ${user.lastName}`;
                });
                this.generateTooltip();
                if (this.editingMode === ContentMode.CREATE) {
                    this.scrollToBottom();
                    this.onSelectInsight(this.selectedInsightId);
                } else if (this.editingMode === ContentMode.UPDATE && this.selectedInsightId == null) {
                    // Special case for initial rendering
                    this.onChangePromtType();
                } else {
                    this.onSelectInsight(this.selectedInsightId);
                    this.scrollToSelected();
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Failed to customInsights:', err);
                this.isLoading = false;
            }
        });

        this.subscriptions.push(requestCustomInsights$);
    }


    private scrollToBottom() {
        try {
            if (this.listScrollPanelElement) {
                requestAnimationFrame(() => {
                    const scrollContent = this.listScrollPanelElement?.sp?.contentViewChild?.nativeElement;
                    if (scrollContent) {
                        this.listScrollPanelElement.scrollTo(scrollContent.scrollHeight, 0, true);
                    }
                });
            }
        } catch {}
    }

    /** Brings the selected prompt into view, as it can sit below the fold of a long list */
    private scrollToSelected() {
        requestAnimationFrame(() => {
            const selectedItem = document.getElementById(`${this.id}_select_item_${this.selectedInsightId}`);
            if (this.listScrollPanelElement && selectedItem) {
                this.listScrollPanelElement.scrollIntoViewNative(selectedItem);
            }
        });
    }

    private generateTooltip(): void {
        [...this.promptsList[CustomInsightType.MENTION_INSIGHTS],
            ...this.promptsList[CustomInsightType.BRIEFING_ANALYSIS_BLOCK],
            ...this.promptsList[CustomInsightType.BRIEFING_CURATOR],
            ...this.promptsList[CustomInsightType.COVERAGE_CURATION]
        ].forEach((item: DialogCustomInsights) => {
            const translateKey = item?.lastModifiedDate ?
                'Briefings.templateManageDialog.tooltipInfo' : 'Briefings.templateManageDialog.tooltipInfo.create.only';
            item.tooltip = this.localizationService.transform(translateKey, {
                createdBy: this.userIdToNameMap[item.createdByUserId],
                createdDate: moment(item.createdDate).format(this.dateFormat),
                modifiedBy: this.userIdToNameMap[item.lastModifiedByUserId],
                modifiedDate: moment(item.lastModifiedDate).format(this.dateFormat)
            });
        });
    }

    private normalize() {
        const normalize = (v?: string) => v?.trim() ?? '';
        this.editingContent = {
            name: normalize(this.editingContent.name),
            prompt: normalize(this.editingContent.prompt)
        };
        this.selectedContent = {
            name: normalize(this.selectedContent.name),
            prompt: normalize(this.selectedContent.prompt)
        };
    }


    onChangePromtType() {
        this.editingMode = ContentMode.UPDATE;
        requestAnimationFrame(() => {
            if (this.listScrollPanelElement) {
                this.listScrollPanelElement.scrollTo(0, 0, true);
            }
            const firstItem = this.promptsList?.[this.selectedPromptType]?.[0];
            this.onSelectInsight(firstItem?.id);
        });
    }

    onSelectInsight(id: number | null) {
        this.editingMode = ContentMode.UPDATE;
        const insight = this.promptsList[this.selectedPromptType].find((item) => item.id === id);
        this.editingContent = {
            name: insight?.name ?? '',
            prompt: insight?.prompt ?? ''
        };
        this.selectedContent = { ...this.editingContent };
        this.normalize();
        this.editingContentUpdated = false;
        this.selectedInsightId = insight ? insight.id : null;
    }

    onShowCreatePromptPanel() {
        this.editingMode = ContentMode.CREATE;
        this.editingContent = {
            name: '',
            prompt: ''
        };

        this.selectedContent = { ...this.editingContent };
        this.editingContentUpdated = false;
        this.selectedInsightId = null;
    }

    onCheckChange() {
        this.normalize();
        const isSame =
            this.editingContent.name === this.selectedContent.name &&
            this.editingContent.prompt === this.selectedContent.prompt

        this.editingContentUpdated = !isSame;
    }

    onUpdateAndSaveInsight() {
        let request$: Observable<number | boolean> | null = null;
        if (this.editingMode === ContentMode.CREATE) {
            const customInsight = new CustomInsights();
            customInsight.name = this.editingContent.name;
            customInsight.prompt = this.editingContent.prompt;
            customInsight.promptType = this.selectedPromptType;
            request$ = this.coverageAnalysisService.createCustomInsight(customInsight, InsightContext.CustomPromptDialog);
        } else if (this.editingMode === ContentMode.UPDATE) {
            const customInsight = this.promptsList[this.selectedPromptType].find((item) => item.id === this.selectedInsightId);
            if (customInsight) {
                customInsight.name = this.editingContent.name;
                customInsight.prompt = this.editingContent.prompt;
                request$ = this.coverageAnalysisService.updateCustomInsight(customInsight, InsightContext.CustomPromptDialog);
            }
        }
        if (request$) {
            request$.subscribe((result) => {
                if (this.editingMode === ContentMode.CREATE && typeof result === 'number') {
                    // ID assigned upon creation
                    this.selectedInsightId = result;
                }
            });
        }
    }

    onDeleteInsight(event: Event, id: number) {
        const list = this.promptsList[this.selectedPromptType];
        const index = list.findIndex(item => item.id === id);
        if (index === -1) {
            return;
        };
        this.editingMode = ContentMode.DELETE;
        const customInsight = list[index];
        const confirmDialogModel: ConfirmDialogModel = {
            inputs: {
                heading: 'Ai.analysis.customInsight.delete',
                showCancel: true,
                message: this.localizationService.transform('Ai.analysis.customInsight.delete.confirm', { name: customInsight.name }),
                okLabel: 'Core.button.delete',
                id: 'CustomPromptDelete'
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.coverageAnalysisService.deleteCustomInsight(customInsight, InsightContext.CustomPromptDialog);
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeDialog();
        }
    }

    onDone() {
        this.closeDialog();
    }
}
